// Opens a chat room at one of its messages: what a mention in the bell
// (chat_mention) and a /netplay/<room id>?message=<message id> link lead to
// (docs/plans/chat-mentions.md). On the netplay page it opens in place. On
// any other page a room the player is in opens in the dock's chat sheet
// (react/dock/chat); a phone, which has no sheet, or another room goes to the
// netplay page.

import { ChatActions } from 'components/ChatActions';
import { ChatRoom } from 'models/ChatRoom';
import { __ } from 'i18n/index';
import { assignPage } from 'navigation/pageLocation';
import { NETPLAY_VIEWS, navigateToView } from 'react/matchmaking/unified/netplayView';
import { isNetplayPageActive } from 'react/netplay/netplayPageState';
import { isPhoneWidth } from 'react/dock/phoneWidth';
import store from 'react/redux/store/store';
import { showToast } from 'react/toasts/showToast';

const ROOM_PATH = /\/netplay\/(\d+)\/?$/;
const MESSAGE_PARAMETER = 'message';

export function roomMessageUrl(chatRoomId, messageId) {
	return `${window.siteUrl}/netplay/${chatRoomId}?${MESSAGE_PARAMETER}=${messageId}`;
}

async function focusMessage(chatRoom, messageId) {
	if(!await chatRoom.chatMessages.focusMessage(messageId)) {
		showToast({ content: __("That message isn't in the room any more.") });
	}
}

async function openInDockChat(chatRoom, messageId) {
	// Loaded on use: components/TokensManager, which calls this, is below the dock in the import graph.
	const { default: openDockChat } = await import(/* webpackChunkName: "dock-chat-open" */ 'react/dock/chat/openDockChat');
	store.dispatch(openDockChat({ conversation: { kind: 'room', id: chatRoom.id } }));
	await focusMessage(chatRoom, messageId);
}

export default async function openRoomAtMessage(chatRoomId, messageId) {
	if(!isNetplayPageActive()) {
		const chatRoom = isPhoneWidth() ? null : ChatRoom.isInChatById(chatRoomId);
		if(chatRoom) {
			await openInDockChat(chatRoom, messageId);
			return;
		}
		assignPage(roomMessageUrl(chatRoomId, messageId));
		return;
	}
	navigateToView(NETPLAY_VIEWS.chat);
	try {
		await ChatActions.joinChatRoomById(chatRoomId);
	} catch(error) {
		console.error('Could not open room', chatRoomId, error);
		return;
	}
	const chatRoom = ChatRoom.isInChatById(chatRoomId);
	if(!chatRoom) {
		return;
	}
	chatRoom.setActive();
	await focusMessage(chatRoom, messageId);
}

// The netplay page loaded from a /netplay/<room id>?message=<message id> link:
// opens the room at the message once, and takes the message out of the URL so
// a reload doesn't jump back to it.
export function openRoomAtMessageFromLocation(rootWindow = window) {
	const url = new URL(rootWindow.location.href);
	const room = ROOM_PATH.exec(url.pathname);
	const messageId = Number.parseInt(url.searchParams.get(MESSAGE_PARAMETER), 10);
	if(!room || !messageId) {
		return Promise.resolve();
	}
	url.searchParams.delete(MESSAGE_PARAMETER);
	rootWindow.history.replaceState(rootWindow.history.state, '', `${url.pathname}${url.search}${url.hash}`);
	return openRoomAtMessage(Number.parseInt(room[1], 10), messageId);
}
